import axios from 'axios';
import { number, object, optional, string, unknown } from 'zod/mini';

const AMPLIFY_ERROR_FIELDS_SCHEMA = object({
	recoverySuggestion: optional(string()),
	metadata: optional(
		object({
			extendedRequestId: optional(string()),
			httpStatusCode: optional(number()),
			requestId: optional(string()),
		}),
	),
	underlyingError: optional(unknown()),
});

const MAXIMUM_NESTED_ERROR_DEPTH = 5;

const MESSAGE_FIELD_SCHEMA = object({ message: optional(string()) });

interface GetLoggableErrorAtDepthArgs {
	readonly err: unknown;
	readonly depth: number;
}

function getLoggableErrorAtDepth({ err, depth }: GetLoggableErrorAtDepthArgs): unknown {
	if (depth > MAXIMUM_NESTED_ERROR_DEPTH) {
		return '[maximum nested error depth reached]';
	}
	const nextDepth = depth + 1;
	if (axios.isAxiosError(err)) {
		return {
			status: err.response?.status,
			statusText: err.response?.statusText,
			code: err.code,
			message: err.message,
			stack: err.stack,
			method: err.config?.method,
			url: err.config?.url?.split('?')[0],
			responseData: err.response?.data,
			cause: getLoggableErrorAtDepth({ err: err.cause, depth: nextDepth }),
		};
	}
	if (err instanceof Error) {
		const { underlyingError, ...amplifyErrorFields } = AMPLIFY_ERROR_FIELDS_SCHEMA.safeParse(err).data ?? {};
		return {
			name: err.name,
			message: err.message,
			stack: err.stack,
			cause: getLoggableErrorAtDepth({ err: err.cause, depth: nextDepth }),
			...amplifyErrorFields,
			underlyingError: getLoggableErrorAtDepth({ err: underlyingError, depth: nextDepth }),
		};
	}
	if (typeof err === 'object' && err !== null) {
		return { type: err.constructor.name, message: MESSAGE_FIELD_SCHEMA.safeParse(err).data?.message };
	}
	return err;
}

export function getLoggableError(err: unknown): unknown {
	return getLoggableErrorAtDepth({ err, depth: 0 });
}
